import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileSpreadsheet, Upload, Download, Loader2, CheckCircle, AlertCircle, BarChart3, TrendingUp, TrendingDown, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SimplePriceAnalysis = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    if (uploadedFile) {
      if (!uploadedFile.name.endsWith('.xlsx') && !uploadedFile.name.endsWith('.xls')) {
        toast.error('يجب رفع ملف Excel فقط (.xlsx أو .xls)');
        return;
      }
      setFile(uploadedFile);
      setResult(null);
      setError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false
  });

  const handleUpload = async () => {
    if (!file) {
      toast.error('الرجاء اختيار ملف أولاً');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/analysis/simple-price-analysis/upload`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setResult(data);
        toast.success(`تم تحليل ${data.total_records} سجل بنجاح`);
      } else {
        throw new Error(data.detail || 'فشل في تحليل الملف');
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async () => {
    if (!result?.task_id) {
      toast.error('الرجاء رفع وتحليل ملف أولاً');
      return;
    }

    setExporting(true);

    try {
      const response = await fetch(`${API_URL}/api/analysis/simple-price-analysis/export-excel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ task_id: result.task_id })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'فشل في تصدير الملف');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `تحليل_الأسعار_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('تم تصدير التقرير بنجاح');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExporting(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setResult(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6" dir="rtl">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 mb-4 shadow-lg shadow-emerald-500/25">
            <BarChart3 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">تحليل الأسعار الشامل</h1>
          <p className="text-slate-400">رفع ملف Excel وتصدير تقرير تحليلي شامل بـ 12 ورقة</p>
        </div>

        {/* Formula Info */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 mb-6 border border-slate-700">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <TrendingUp className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">معادلة حساب التوفير والخسارة</h3>
              <p className="text-slate-400 text-sm">
                التوفير/الخسارة = (سعر أول شراء - السعر الصافي الحالي) × الكمية
              </p>
              <p className="text-slate-500 text-xs mt-1">
                * السعر الصافي = صافي المبلغ ÷ الكمية
              </p>
            </div>
          </div>
        </div>

        {/* Upload Area */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 mb-6">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300 ${
              isDragActive
                ? 'border-emerald-500 bg-emerald-500/10'
                : file
                ? 'border-emerald-500/50 bg-emerald-500/5'
                : 'border-slate-600 hover:border-slate-500 hover:bg-slate-700/30'
            }`}
          >
            <input {...getInputProps()} data-testid="file-input" />
            
            {file ? (
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-3">
                  <FileSpreadsheet className="w-7 h-7 text-emerald-400" />
                </div>
                <p className="text-white font-medium mb-1">{file.name}</p>
                <p className="text-slate-400 text-sm">
                  {(file.size / 1024).toFixed(1)} كيلوبايت
                </p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    resetForm();
                  }}
                  className="mt-3 text-sm text-slate-400 hover:text-white transition-colors"
                >
                  اختيار ملف آخر
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 rounded-xl bg-slate-700 flex items-center justify-center mb-3">
                  <Upload className="w-7 h-7 text-slate-400" />
                </div>
                <p className="text-white font-medium mb-1">
                  {isDragActive ? 'أفلت الملف هنا' : 'اسحب ملف Excel هنا'}
                </p>
                <p className="text-slate-400 text-sm">أو اضغط لاختيار ملف</p>
                <p className="text-slate-500 text-xs mt-2">يدعم ملفات .xlsx و .xls</p>
              </div>
            )}
          </div>

          {/* Upload Button */}
          {file && !result && (
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="w-full mt-4 py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium hover:from-emerald-600 hover:to-teal-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="upload-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  جاري التحليل...
                </>
              ) : (
                <>
                  <BarChart3 className="w-5 h-5" />
                  تحليل الملف
                </>
              )}
            </button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-400 font-medium">خطأ في التحليل</p>
              <p className="text-red-300/70 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 space-y-6">
            {/* Success Header */}
            <div className="flex items-center gap-3 pb-4 border-b border-slate-700">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-white font-medium">تم التحليل بنجاح</p>
                <p className="text-slate-400 text-sm">الملف جاهز للتصدير</p>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-white">{result.total_records}</p>
                <p className="text-slate-400 text-sm">إجمالي السجلات</p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-emerald-400">{result.unique_items}</p>
                <p className="text-slate-400 text-sm">عدد الأصناف</p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-blue-400">{result.columns_found?.length || 0}</p>
                <p className="text-slate-400 text-sm">الأعمدة المكتشفة</p>
              </div>
              <div className="bg-slate-700/50 rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-purple-400">12</p>
                <p className="text-slate-400 text-sm">أوراق التقرير</p>
              </div>
            </div>

            {/* Columns Found */}
            {result.columns_found && result.columns_found.length > 0 && (
              <div>
                <p className="text-slate-400 text-sm mb-2">الأعمدة المكتشفة:</p>
                <div className="flex flex-wrap gap-2">
                  {result.columns_found.map((col, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 rounded-full bg-slate-700 text-slate-300 text-sm"
                    >
                      {col}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Report Sheets Info */}
            <div className="bg-slate-700/30 rounded-xl p-4">
              <p className="text-white font-medium mb-3">أوراق التقرير:</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                {[
                  { name: 'البيانات', icon: '📊' },
                  { name: 'تحليل الأصناف', icon: '📦' },
                  { name: 'حساب التوفير والخسارة', icon: '💰' },
                  { name: 'التحليل الزمني', icon: '📅' },
                  { name: 'ملخص التغييرات', icon: '📈' },
                  { name: 'التوفير', icon: '🟢' },
                  { name: 'الخسارة', icon: '🔴' },
                  { name: 'التغييرات الشهرية', icon: '📆' },
                  { name: 'أكثر الأصناف تغيراً', icon: '📊' },
                  { name: 'أكثر الأصناف توفيراً', icon: '🏆' },
                  { name: 'أكثر الأصناف خسارة', icon: '⚠️' },
                  { name: 'ملخص عام', icon: '📋' }
                ].map((sheet, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-slate-300">
                    <span>{sheet.icon}</span>
                    <span>{sheet.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Export Button */}
            <button
              onClick={handleExport}
              disabled={exporting}
              className="w-full py-4 px-4 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg"
              data-testid="export-btn"
            >
              {exporting ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  جاري التصدير...
                </>
              ) : (
                <>
                  <FileDown className="w-6 h-6" />
                  تصدير التقرير Excel
                </>
              )}
            </button>

            {/* Info Note */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-start gap-3">
              <TrendingUp className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="text-blue-300">التقرير يحتوي على معادلات Excel ديناميكية</p>
                <p className="text-blue-300/70 mt-1">
                  عند تعديل أي سعر في ورقة "البيانات"، ستتحدث جميع الأوراق الأخرى تلقائياً
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-slate-500 text-sm">
          تطوير محمد مهدي
        </div>
      </div>
    </div>
  );
};

export default SimplePriceAnalysis;
